<script lang="ts">import NavigationCategoryGrid from '../NavigationCategoryGrid/NavigationCategoryGrid.svelte';
import ImageListCategory from '../../../shared/ImageListCategory/ImageListCategory.svelte';
import { TIMEOUT_ENTER_DURATION, TIMEOUT_LEAVE_DURATION } from '../../../../../js-v2/constants';
import { activeDropdownStore } from '../../../../../js-v2/stores/desktop-nav-stores';
import { getLogo } from '../../../../../js-v2/stores/logo-store';
import { hoverWithDelay } from '../../../../../js-v2/hoverWithDelay';
import { onTouchOrClick } from '../../../../../js-v2/onTouchOrClick';
import { getTracking } from '../../../../../js-v2/tracking';
import { getLocation, getNavDeviceType } from '../../../../../js-v2/trackingUtils';
import { getUrlPath } from '../../../../../js-v2/urlUtils';
const { item } = $props();
let dropdownContentClientHeight = $state();
let dropdownEl = $state();
const trackFn = getTracking();
// Get logo from context and calculate dropdown padding
const logoStore = getLogo();
function open() {
    activeDropdownStore.open(item.contentfulEntryId);
    trackFn({
        eventName: 'Open Menu',
        properties: {
            currentPage: getUrlPath(),
            menuId: item.contentfulEntryId,
            menuText: item.props.title,
            menuCategory: item.props.title,
            menuLocation: getLocation(dropdownEl),
            navDeviceType: getNavDeviceType(dropdownEl),
        },
    });
}
function close() {
    if ($activeDropdownStore.id !== item.contentfulEntryId) {
        return;
    }
    trackFn({
        eventName: 'Close Menu',
        properties: {
            currentPage: getUrlPath(),
            menuId: item.contentfulEntryId,
            menuText: item.props.title,
            menuCategory: item.props.title,
            menuLocation: getLocation(dropdownEl),
            navDeviceType: getNavDeviceType(dropdownEl),
        },
    });
    activeDropdownStore.close();
}
function toggle() {
    $activeDropdownStore.id === item.contentfulEntryId ? close() : open();
}
const isOpen = $derived($activeDropdownStore.id === item.contentfulEntryId);
$effect(() => {
    if (isOpen && dropdownContentClientHeight !== $activeDropdownStore.contentHeight) {
        activeDropdownStore.updateContentHeight(dropdownContentClientHeight);
    }
});
</script>

<div
  bind:this={dropdownEl}
  class="Dropdown"
  data-category={item.props.title}
  use:hoverWithDelay={[open, close, TIMEOUT_ENTER_DURATION, TIMEOUT_LEAVE_DURATION]}
>
  <button
    class="DropdownToggle"
    data-entry-id={item.contentfulEntryId}
    aria-haspopup="true"
    aria-expanded={isOpen}
    aria-controls={`global-nav-desktop-dropdown-content-${item.contentfulEntryId}`}
    use:onTouchOrClick={toggle}
  >
    <span>{item.props.title}</span>
  </button>

  <div
    id="global-nav-desktop-dropdown-content-{item.contentfulEntryId}"
    class="DropdownContent"
    class:active={isOpen}
    class:logo-lockup-padding-left={$logoStore === 'LogoLockup'}
    bind:clientHeight={dropdownContentClientHeight}
  >
    {#if item.componentName === 'PublicWebNavigationImageListCategoryV2'}
      <div class="image-list-container">
        <ImageListCategory
          id={item.contentfulEntryId}
          title={item.props?.title}
          navItems={item.props?.navItems}
          alignment="center"
          containerSize="large"
        />
      </div>
    {:else}
      <NavigationCategoryGrid navItem={item} />
    {/if}
  </div>
</div>

<style>@keyframes fade-in-slide-from-top {
  from {
    opacity: 0;
    transform: translateX(0) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes fade-in-slide-from-left {
  from {
    opacity: 0;
    transform: translateX(-4px) translateY(0);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes slide-down {
  from {
    transform: translateY(-50%);
  }
  to {
    transform: translateY(0);
  }
}
/**
We use rem units for media queries to promote accessible layouts. By utilizing rem units, the computed values in media queries are determined by the user's browser-defined font size rather than our global 'font-size: 62.5%' reset. This approach ensures that our design adapts to individual user settings, enhancing accessibility. 
*/
.DropdownToggle {
  --letter-spacing: var(--text-variant-100-letter-spacing);
  --line-height: var(--text-variant-100-line-height);
  --font-size: var(--text-variant-100-font-size);
  --font-weight: var(--text-variant-100-font-weight);
  --font-family: var(--text-font-family);
  --max-width: var(--text-variant-100-max-width);
  color: var(--color-text);
  font-family: var(--font-family);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  letter-spacing: var(--letter-spacing);
  line-height: var(--line-height);
  max-width: var(--max-width);
  cursor: pointer;
  appearance: none;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  display: inline-block;
  width: auto;
  height: auto;
  min-height: 0;
  max-height: none;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 10px 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--color-text, var(--color-neutral-000, #000000));
  letter-spacing: normal;
  text-align: center;
  vertical-align: middle;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-tap-highlight-color: transparent;
  padding: 18px 10px;
}
.DropdownToggle:focus-visible:not(:active) {
  outline: var(--accessibility-focus-outline-width) var(--accessibility-focus-outline-style, solid) var(--accessibility-focus-outline-color);
  border-radius: var(--accessibility-focus-outline-border-radius);
  outline-offset: var(--accessibility-focus-outline-offset);
}
.DropdownToggle:focus {
  background: transparent;
  outline: none;
}
.DropdownToggle:hover {
  background: transparent;
}

.DropdownContent {
  padding-left: 80px;
  padding-right: 80px;
  box-sizing: border-box;
  position: absolute !important;
  left: 0;
  width: 100%;
  display: none;
  padding: 40px 80px 80px 80px;
  top: calc(var(--desktop-nav-height) - 4px);
}
@media (min-width: 105rem) {
  .DropdownContent {
    --nav-outer-gutter-base: calc(var(--space-10) / 2);
    --nav-outer-gutter: max(var(--nav-outer-gutter-base), var(--dynamic-gutter));
    padding-left: calc(80px + var(--nav-outer-gutter)) !important;
    padding-right: calc(80px + var(--nav-outer-gutter)) !important;
  }
}
.DropdownContent.active {
  display: block;
}
.DropdownContent.logo-lockup-padding-left {
  padding-left: 58px;
  padding-right: 58px;
  padding-left: 58px;
}
@media (min-width: 105rem) {
  .DropdownContent.logo-lockup-padding-left {
    --nav-outer-gutter-base: calc(var(--space-10) / 2);
    --nav-outer-gutter: max(var(--nav-outer-gutter-base), var(--dynamic-gutter));
    padding-left: calc(58px + var(--nav-outer-gutter)) !important;
    padding-right: calc(58px + var(--nav-outer-gutter)) !important;
  }
}

.image-list-container :global(.ImageListCategory .list-container) {
  margin: 0 auto;
}</style>
